import { BEIGE_500, GRAY_900, WHITE } from 'constants/styling/theme'

import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import { FC } from 'react'
import { OverviewMode } from '../../helpers/conversationOverview'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

interface Props {
  value: OverviewMode
  onChange: (mode: OverviewMode) => void
}

const MODES: OverviewMode[] = [OverviewMode.STATUS, OverviewMode.ORDER]

const INACTIVE_LABEL_COLOR = '#5B5F63'
const ACTIVE_SEGMENT_SHADOW = '0 1px 4px rgba(19, 24, 29, 0.08)'
const SLIDE_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

const MODE_LABEL_KEY: Record<OverviewMode, string> = {
  [OverviewMode.STATUS]: 'chat:overview.by_status',
  [OverviewMode.ORDER]: 'chat:overview.by_order',
}

export const OverviewGroupingToggle: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation(['chat'])
  const activeIndex = MODES.indexOf(value)

  return (
    <Box
      role="group"
      aria-label={t('chat:overview.group_by')}
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '0.4rem',
        padding: '0.4rem',
        borderRadius: '4rem',
        backgroundColor: BEIGE_500,
      }}
    >

      <Box
        aria-hidden
        data-testid="overview-grouping-indicator"
        sx={{
          position: 'absolute',
          top: '0.4rem',
          bottom: '0.4rem',
          left: '0.4rem',
          width: 'calc(50% - 0.6rem)',
          borderRadius: '4rem',
          backgroundColor: WHITE,
          boxShadow: ACTIVE_SEGMENT_SHADOW,
          transform: `translateX(calc(${activeIndex * 100}% + ${activeIndex * 0.4}rem))`,
          transition: `transform 300ms ${SLIDE_EASING}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />

      {MODES.map((mode) => {
        const isActive = mode === value

        return (
          <ButtonBase
            key={mode}
            aria-pressed={isActive}
            onClick={() => onChange(mode)}
            sx={{ position: 'relative', zIndex: 1, height: '4rem', borderRadius: '4rem' }}
          >
            <Typography
              variant="text-sm"
              fontWeight={isActive ? 600 : 500}
              color={isActive ? GRAY_900 : INACTIVE_LABEL_COLOR}
              sx={{ transition: `color 300ms ${SLIDE_EASING}` }}
            >
              {t(MODE_LABEL_KEY[mode])}
            </Typography>
          </ButtonBase>
        )
      })}

    </Box>
  )
}
